import { useState, useMemo, useEffect, useRef, Suspense, lazy } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "./Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowLeft, Check, Loader2, CalendarDays, MapPin, PartyPopper, Mail } from "lucide-react";

const Calendar = lazy(() => import("@/components/ui/calendar").then((m) => ({ default: m.Calendar })));

const SIZES = [
  { id: "1 Car", low: 300, high: 450 },
  { id: "2 Car", low: 500, high: 750 },
  { id: "3 Car", low: 750, high: 1100 },
  { id: "4 Car", low: 1000, high: 1450 },
];
const CLUTTER = [
  { id: "Light Clutter", mult: 1.0 },
  { id: "Moderate Clutter", mult: 1.3 },
  { id: "Very Cluttered", mult: 1.75 },
];
const CORE = [
  { id: "Junk Removal / Haul Away", low: 300, high: 500 },
  { id: "Deep Clean & Organize", low: 250, high: 425 },
];
const ADDONS = [
  { key: "lights", label: "LED Garage Lights", options: [
    { label: "None", price: 0 }, { label: "1 Light", price: 50 },
    { label: "2 Lights (Recommended Min.)", price: 100 }, { label: "3 Lights", price: 150 }, { label: "4 Lights", price: 200 } ] },
  { key: "storage", label: "Storage Options", options: [
    { label: "None", price: 0 }, { label: "Heavy-Duty Rack", price: 200 },
    { label: "Plastic Rack", price: 100 }, { label: "Storage Tote", price: 20 } ] },
  { key: "door", label: "Garage Door Tune-Up", options: [
    { label: "None", price: 0 }, { label: "Single Door", price: 50 }, { label: "Double Door", price: 75 },
    { label: "3-Car Garage Doors", price: 115 }, { label: "4-Car Garage Doors", price: 155 } ] },
  { key: "pegboard", label: "Pegboard Tool Wall", options: [
    { label: "None", price: 0 }, { label: "Small", price: 50 }, { label: "Medium", price: 75 }, { label: "Large", price: 100 } ] },
  { key: "hooks", label: "Wall Hook Package", options: [
    { label: "None", price: 0 }, { label: "5 Hooks Installed", price: 30 },
    { label: "Basic Extra Hook", price: 3, per: true }, { label: "Heavy-Duty Extra Hook", price: 6, per: true } ] },
];
const TIMES = ["8:00 AM", "10:00 AM", "12:00 PM", "2:00 PM", "4:00 PM"];
const round25 = (v) => Math.round(v / 25) * 25;
const label = (o) => (o.price === 0 ? o.label : o.per ? `${o.label} — $${o.price} each` : `${o.label} — $${o.price}`);

// Change the URL (for Facebook/GA tracking) without a full reload + fire Meta Pixel event
const trackConversion = (path, event) => {
  try { window.history.pushState({}, "", path); } catch (e) { /* noop */ }
  if (window.fbq) window.fbq("track", event);
};

const STEP_LABELS = ["Size", "Clutter", "Services", "Add-Ons", "Your Info", "Book"];
const STEP_NUDGE = {
  1: "Quick start — just tap your garage size.",
  2: "Nice. A couple more taps to go.",
  3: "You're halfway there.",
  4: "Almost done — add-ons are optional.",
  5: "Last step to unlock your price.",
  6: "All set — pick a time that works for you.",
};
const STEP_EVENT = {
  1: "EstimatorStarted", 2: "EstimatorSize", 3: "EstimatorClutter",
  4: "EstimatorServices", 5: "EstimatorContactViewed", 6: "EstimatorBooking",
};

export const PriceEstimator = () => {
  const [step, setStep] = useState(1);
  const [size, setSize] = useState(null);
  const [clutter, setClutter] = useState(null);
  const [services, setServices] = useState([]);
  const [addonSel, setAddonSel] = useState({ lights: 0, storage: 0, door: 0, pegboard: 0, hooks: 0 });
  const [hookQty, setHookQty] = useState(1);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", zip: "" });
  const [leadId, setLeadId] = useState(null);
  const [booking, setBooking] = useState({ date: null, time: null, address: "", city: "", zip: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const debounceRef = useRef(null);
  const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    const name = STEP_EVENT[step];
    if (name && window.fbq) window.fbq("trackCustom", name);
  }, [step]);

  const fetchSuggestions = async (input) => {
    if (!GMAPS_KEY || input.trim().length < 3) { setAddrSuggestions([]); return; }
    try {
      const res = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Goog-Api-Key": GMAPS_KEY },
        body: JSON.stringify({ input, includedRegionCodes: ["us"], includedPrimaryTypes: ["street_address", "premise", "subpremise"] }),
      });
      const data = await res.json();
      setAddrSuggestions((data.suggestions || []).map((s) => s.placePrediction).filter(Boolean).slice(0, 5));
    } catch { setAddrSuggestions([]); }
  };

  const onAddressChange = (v) => {
    setBooking((b) => ({ ...b, address: v }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 300);
  };

  const selectSuggestion = async (pred) => {
    setAddrSuggestions([]);
    const fallback = pred.structuredFormat?.mainText?.text || pred.text?.text || "";
    try {
      const res = await fetch(`https://places.googleapis.com/v1/places/${pred.placeId}?languageCode=en`, {
        headers: { "X-Goog-Api-Key": GMAPS_KEY, "X-Goog-FieldMask": "addressComponents" },
      });
      const place = await res.json();
      const get = (t) => place.addressComponents?.find((c) => c.types.includes(t));
      const streetNo = get("street_number")?.longText || "";
      const route = get("route")?.longText || "";
      const city = get("locality")?.longText || get("sublocality")?.longText || get("postal_town")?.longText || "";
      const zip = get("postal_code")?.longText || "";
      setBooking((b) => ({ ...b, address: `${streetNo} ${route}`.trim() || fallback, city: city || b.city, zip: zip || b.zip }));
    } catch {
      setBooking((b) => ({ ...b, address: fallback || b.address }));
    }
  };

  const price = useMemo(() => {
    if (!size) return null;
    const s = SIZES.find((x) => x.id === size);
    const c = CLUTTER.find((x) => x.id === clutter);
    const mult = c ? c.mult : 1.0;
    const coreLow = services.reduce((a, id) => a + (CORE.find((x) => x.id === id)?.low || 0), 0);
    const coreHigh = services.reduce((a, id) => a + (CORE.find((x) => x.id === id)?.high || 0), 0);
    const baseLow = round25((s.low + coreLow) * mult);
    const baseHigh = round25((s.high + coreHigh) * mult);
    let addonTotal = 0;
    const selectedAddons = [];
    ADDONS.forEach((a) => {
      const opt = a.options[addonSel[a.key]];
      if (opt && opt.price > 0) {
        const qty = opt.per ? hookQty : 1;
        const cost = opt.price * qty;
        addonTotal += cost;
        selectedAddons.push(`${a.label}: ${opt.label}${opt.per ? ` × ${qty}` : ""} ($${cost})`);
      }
    });
    return { baseLow, baseHigh, addonTotal, totalLow: baseLow + addonTotal, totalHigh: baseHigh + addonTotal, selectedAddons };
  }, [size, clutter, services, addonSel, hookQty]);

  const toggleService = (id) => setServices((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const canNext = () => {
    if (step === 1) return !!size;
    if (step === 2) return !!clutter;
    if (step === 3) return services.length > 0;
    return true;
  };

  const submitEstimate = async (e) => {
    e.preventDefault();
    if (!price) return;
    setLoading(true);
    try {
      const { data } = await api.post("/estimate", {
        ...contact, garage_size: size, clutter, core_services: services, addons: price.selectedAddons,
        base_low: price.baseLow, base_high: price.baseHigh, addon_total: price.addonTotal,
        total_low: price.totalLow, total_high: price.totalHigh,
      });
      setLeadId(data.id);
      setRevealed(true);
      setBooking((b) => ({ ...b, zip: contact.zip || "" }));
      trackConversion("/price-estimate-completed", "Lead");
      if (window.fbq) window.fbq("trackCustom", "EstimatorSubmitted");
      toast.success("Here's your estimate! Now pick a time.");
      setStep(6);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!booking.date || !booking.time || !booking.address.trim() || !booking.city.trim()) {
      toast.error("Pick a day, time and enter your street address and city."); return;
    }
    const fullAddress = `${booking.address.trim()}, ${booking.city.trim()}, MN${booking.zip.trim() ? ` ${booking.zip.trim()}` : ""}`;
    setLoading(true);
    try {
      await api.post(`/estimate/${leadId}/booking`, {
        date: new Date(booking.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }),
        time: booking.time, address: fullAddress,
      });
      setDone(true);
      trackConversion("/appointment-booked", "Schedule");
      toast.success("Appointment booked!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed.");
    } finally { setLoading(false); }
  };

  const OptBtn = ({ active, onClick, testid, children }) => (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`w-full text-left px-5 py-4 border transition-colors font-medium ${
        active ? "bg-ink text-paper border-ink" : "bg-surface border-ink/15 text-ink hover:border-rust"}`}>
      <span className="flex items-center justify-between">{children}
        {active && <Check size={18} className="text-rust" />}</span>
    </button>
  );

  return (
    <section id="quote" className="bg-cream pt-6 pb-20 lg:pt-14 lg:pb-28">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        {!done && (
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-rust">Instant Estimator</span>
            <p className="mt-3 font-display font-bold text-2xl sm:text-3xl text-ink" data-testid="step-nudge">
              {STEP_NUDGE[step]}
            </p>
          </Reveal>
        )}

        {/* progress */}
        {!done && (
          <div className="mt-8 flex items-center gap-2" data-testid="estimator-progress">
            {STEP_LABELS.map((l, i) => (
              <div key={l} title={l} data-testid={`progress-step-${i + 1}`}
                className={`h-2.5 flex-1 rounded-sm transition-colors ${
                  step === i + 1 ? "bg-rust" : step > i + 1 ? "bg-ink" : "bg-ink/15"}`} />
            ))}
          </div>
        )}

        <div className="mt-10 grid lg:grid-cols-12 gap-8">
          {/* MAIN */}
          <div className="lg:col-span-7">
            {done ? (
              <div data-testid="booking-confirmed" className="border border-ink/15 bg-surface p-10 lg:p-14 text-center">
                <PartyPopper size={48} className="text-rust mx-auto" />
                <h3 className="mt-6 font-display font-bold text-3xl text-ink">You're booked!</h3>
                <p className="mt-3 text-ink/60">We've sent the details over and we'll confirm shortly. Can't wait to transform your garage.</p>
              </div>
            ) : (
              <div className="border border-ink/15 bg-surface p-6 lg:p-8">
                {step === 1 && (
                  <div>
                    <h3 className="font-display font-bold text-2xl text-ink">How big is your garage?</h3>
                    <div className="mt-6 grid sm:grid-cols-2 gap-3">
                      {SIZES.map((s) => <OptBtn key={s.id} testid={`size-${s.id.replace(/\s/g, "-")}`} active={size === s.id} onClick={() => setSize(s.id)}>{s.id}</OptBtn>)}
                    </div>
                  </div>
                )}
                {step === 2 && (
                  <div>
                    <h3 className="font-display font-bold text-2xl text-ink">How messy is your garage?</h3>
                    <div className="mt-6 space-y-3">
                      {CLUTTER.map((c) => <OptBtn key={c.id} testid={`clutter-${c.id.replace(/\s/g, "-")}`} active={clutter === c.id} onClick={() => setClutter(c.id)}>{c.id}</OptBtn>)}
                    </div>
                  </div>
                )}
                {step === 3 && (
                  <div>
                    <h3 className="font-display font-bold text-2xl text-ink">What service(s) are you looking for?</h3>
                    <p className="mt-2 text-sm text-ink/50">Select one or more.</p>
                    <div className="mt-6 space-y-3">
                      {CORE.map((s) => <OptBtn key={s.id} testid={`service-${s.id.replace(/[\s/]+/g, "-")}`} active={services.includes(s.id)} onClick={() => toggleService(s.id)}>{s.id}</OptBtn>)}
                    </div>
                  </div>
                )}
                {step === 4 && (
                  <div>
                    <h3 className="font-display font-bold text-2xl text-ink">Any add-ons?</h3>
                    <p className="mt-2 text-sm text-ink/50">Optional — leave as "None" to skip.</p>
                    <div className="mt-6 space-y-5">
                      {ADDONS.map((a) => (
                        <div key={a.key}>
                          <Label className="font-mono text-xs uppercase tracking-widest text-ink/60">{a.label}</Label>
                          <select data-testid={`addon-${a.key}`} value={addonSel[a.key]} onChange={(e) => { const v = Number(e.target.value); setAddonSel((p) => ({ ...p, [a.key]: v })); if (a.key === "hooks") setHookQty(1); }}
                            className="mt-2 w-full h-12 bg-surface border border-ink/20 px-3 text-base text-ink focus:outline-none focus:ring-2 focus:ring-rust">
                            {a.options.map((o, i) => <option key={o.label} value={i}>{label(o)}</option>)}
                          </select>
                          {a.key === "hooks" && a.options[addonSel.hooks]?.per && (
                            <div className="mt-2 flex items-center gap-3">
                              <Label className="text-sm text-ink/60">Quantity</Label>
                              <Input type="number" min={1} data-testid="hook-qty" value={hookQty}
                                onChange={(e) => setHookQty(Math.max(1, Number(e.target.value) || 1))}
                                className="w-24 bg-surface border-ink/20 focus-visible:ring-rust" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {step === 5 && (
                  <form onSubmit={submitEstimate} data-testid="estimate-contact-form">
                    <h3 className="font-display font-bold text-2xl text-ink">Get your full estimate emailed to you — and book your visit.</h3>
                    <div className="mt-3 flex items-start gap-2 text-sm text-ink/60 bg-rust/5 border border-rust/15 p-3" data-testid="contact-reassurance">
                      <Mail size={15} className="text-rust mt-0.5 shrink-0" />
                      <span>We'll email you this itemized estimate and use your details only to schedule your visit. No spam, no obligation.</span>
                    </div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-4">
                      <div><Label className="font-mono text-xs uppercase tracking-widest text-ink/60">Name</Label>
                        <Input required data-testid="est-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="mt-2 h-12 text-base bg-surface border-ink/20 focus-visible:ring-rust" /></div>
                      <div><Label className="font-mono text-xs uppercase tracking-widest text-ink/60">Phone</Label>
                        <Input required type="tel" data-testid="est-phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="mt-2 h-12 text-base bg-surface border-ink/20 focus-visible:ring-rust" /></div>
                      <div><Label className="font-mono text-xs uppercase tracking-widest text-ink/60">Email</Label>
                        <Input required type="email" data-testid="est-email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="mt-2 h-12 text-base bg-surface border-ink/20 focus-visible:ring-rust" /></div>
                      <div><Label className="font-mono text-xs uppercase tracking-widest text-ink/60">Zip Code <span className="text-ink/35 normal-case tracking-normal">(optional)</span></Label>
                        <Input data-testid="est-zip" value={contact.zip} onChange={(e) => setContact({ ...contact, zip: e.target.value })} className="mt-2 h-12 text-base bg-surface border-ink/20 focus-visible:ring-rust" /></div>
                    </div>
                    <button type="submit" disabled={loading} data-testid="see-estimate-btn"
                      className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-ink text-paper px-8 py-4 text-base font-semibold hover:bg-rust transition-colors disabled:opacity-60">
                      {loading ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <>Email My Estimate & Book a Visit <ArrowRight size={18} /></>}
                    </button>
                  </form>
                )}
                {step === 6 && price && (
                  <div data-testid="estimate-reveal" className="mb-8 border border-rust/30 bg-rust/5 p-6">
                    <p className="font-mono text-xs uppercase tracking-widest text-rust">Your Instant Estimate</p>
                    <div className="mt-4 space-y-2 text-sm text-ink/70">
                      <div className="flex justify-between"><span>Base Service Estimated Range</span><span className="font-semibold text-ink">${price.baseLow} – ${price.baseHigh}</span></div>
                      <div className="flex justify-between"><span>Selected Add-Ons</span><span className="font-semibold text-ink">+${price.addonTotal}</span></div>
                      <div className="flex justify-between items-baseline pt-3 mt-1 border-t border-rust/20">
                        <span className="font-display font-bold text-ink">Estimated Total Range</span>
                        <span className="font-display font-black text-2xl text-rust">${price.totalLow} – ${price.totalHigh}</span>
                      </div>
                    </div>
                  </div>
                )}
                {step === 6 && (
                  <form onSubmit={submitBooking} data-testid="booking-form">
                    <h3 className="font-display font-bold text-2xl text-ink flex items-center gap-2"><CalendarDays size={22} className="text-rust" /> Book your appointment</h3>
                    <div className="mt-6 grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label className="font-mono text-xs uppercase tracking-widest text-ink/60">Choose a day</Label>
                        <div className="mt-2 border border-ink/15 inline-block bg-surface">
                          <Suspense fallback={<div className="p-6 text-sm text-ink/40 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading calendar…</div>}>
                            <Calendar mode="single" selected={booking.date} onSelect={(d) => setBooking((b) => ({ ...b, date: d }))}
                              disabled={{ before: new Date() }} data-testid="booking-calendar" />
                          </Suspense>
                        </div>
                      </div>
                      <div>
                        <Label className="font-mono text-xs uppercase tracking-widest text-ink/60">Pick a time</Label>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {TIMES.map((t) => (
                            <button key={t} type="button" data-testid={`time-${t.replace(/[\s:]/g, "")}`} onClick={() => setBooking((b) => ({ ...b, time: t }))}
                              className={`px-3 py-3.5 text-sm font-medium border transition-colors ${booking.time === t ? "bg-rust text-white border-rust" : "border-ink/20 text-ink/80 hover:border-ink"}`}>{t}</button>
                          ))}
                        </div>
                        <Label className="mt-6 block font-mono text-xs uppercase tracking-widest text-ink/60">Service address</Label>
                        <div className="relative">
                          <Input data-testid="booking-address" autoComplete="off" value={booking.address}
                            onChange={(e) => onAddressChange(e.target.value)}
                            onBlur={() => setTimeout(() => setAddrSuggestions([]), 150)}
                            placeholder="123 Main St" className="mt-2 h-12 text-base bg-surface border-ink/20 focus-visible:ring-rust" />
                          {addrSuggestions.length > 0 && (
                            <ul data-testid="address-suggestions" className="absolute z-30 left-0 right-0 mt-1 bg-surface border border-ink/20 shadow-lg max-h-64 overflow-auto">
                              {addrSuggestions.map((p) => (
                                <li key={p.placeId}>
                                  <button type="button" onMouseDown={(e) => { e.preventDefault(); selectSuggestion(p); }}
                                    className="w-full text-left px-3 py-2.5 text-sm text-ink hover:bg-ink hover:text-paper transition-colors flex items-start gap-2">
                                    <MapPin size={14} className="text-rust mt-0.5 shrink-0" />
                                    <span>{p.text?.text || p.structuredFormat?.mainText?.text}</span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-6 gap-3">
                          <div className="sm:col-span-3">
                            <Label className="block font-mono text-[10px] uppercase tracking-widest text-ink/50">City</Label>
                            <Input data-testid="booking-city" value={booking.city} onChange={(e) => setBooking((b) => ({ ...b, city: e.target.value }))}
                              placeholder="Minneapolis" className="mt-1.5 h-12 text-base bg-surface border-ink/20 focus-visible:ring-rust" />
                          </div>
                          <div className="sm:col-span-1">
                            <Label className="block font-mono text-[10px] uppercase tracking-widest text-ink/50">State</Label>
                            <Input data-testid="booking-state" value="MN" readOnly disabled className="mt-1.5 h-12 text-base bg-ink/5 border-ink/20 text-ink/60" />
                          </div>
                          <div className="sm:col-span-2">
                            <Label className="block font-mono text-[10px] uppercase tracking-widest text-ink/50">Zip</Label>
                            <Input data-testid="booking-zip" value={booking.zip} onChange={(e) => setBooking((b) => ({ ...b, zip: e.target.value }))}
                              placeholder="55401" className="mt-1.5 h-12 text-base bg-surface border-ink/20 focus-visible:ring-rust" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <button type="submit" disabled={loading} data-testid="book-appointment-btn"
                      className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-ink text-paper px-8 py-4 font-semibold hover:bg-rust transition-colors disabled:opacity-60">
                      {loading ? <><Loader2 size={18} className="animate-spin" /> Booking…</> : <>Book My Appointment <MapPin size={18} /></>}
                    </button>
                  </form>
                )}

                {/* nav */}
                {step >= 1 && step <= 4 && (
                  <div className="mt-8 flex justify-between">
                    <button type="button" data-testid="est-back" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}
                      className="inline-flex items-center gap-2 px-5 py-3 border border-ink/20 font-medium text-ink hover:border-ink transition-colors disabled:opacity-40">
                      <ArrowLeft size={16} /> Back
                    </button>
                    <button type="button" data-testid="est-next" onClick={() => canNext() ? setStep((s) => s + 1) : toast.error("Please make a selection to continue.")}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-ink text-paper font-semibold hover:bg-rust transition-colors">
                      {step === 4 ? "Continue" : "Continue"} <ArrowRight size={16} />
                    </button>
                  </div>
                )}
                {step === 6 && (
                  <button type="button" data-testid="booking-back" onClick={() => setStep(5)}
                    className="mt-4 inline-flex items-center gap-2 text-sm text-ink/60 hover:text-ink transition-colors"><ArrowLeft size={14} /> Back to estimate</button>
                )}
              </div>
            )}
          </div>

          {/* SUMMARY */}
          <div className="lg:col-span-5">
            <div className="border border-ink/15 bg-surface p-6 lg:p-8 lg:sticky lg:top-28" data-testid="estimate-summary">
              <p className="font-mono text-xs uppercase tracking-widest text-ink/60">Your Estimate</p>
              <div className="mt-5 space-y-2 text-sm text-ink/70">
                <div className="flex justify-between"><span>Garage</span><span className="font-medium text-ink">{size || "—"}</span></div>
                <div className="flex justify-between"><span>Clutter</span><span className="font-medium text-ink">{clutter || "—"}</span></div>
                <div className="flex justify-between gap-4"><span>Services</span><span className="font-medium text-ink text-right">{services.length ? services.join(", ") : "—"}</span></div>
              </div>

              {price ? (
                <div className="mt-6 pt-6 border-t border-ink/10 space-y-3" data-testid="summary-live">
                  <div className="flex justify-between text-ink/70"><span>Base Service Range</span><span className="font-semibold text-ink" data-testid="summary-base">${price.baseLow} – ${price.baseHigh}</span></div>
                  <div className="flex justify-between text-ink/70"><span>Selected Add-Ons</span><span className="font-semibold text-ink" data-testid="summary-addons">+${price.addonTotal}</span></div>
                  {price.selectedAddons.length > 0 && (
                    <ul className="text-xs text-ink/50 space-y-1 pl-1">{price.selectedAddons.map((a) => <li key={a}>• {a}</li>)}</ul>
                  )}
                  <div className="flex justify-between items-baseline pt-3 border-t border-ink/10">
                    <span className="font-display font-bold text-ink">Estimated Total</span>
                    <span className="font-display font-black text-2xl text-rust" data-testid="summary-total">${price.totalLow} – ${price.totalHigh}</span>
                  </div>
                  <p className="text-[11px] text-ink/45 leading-relaxed pt-1">Live estimate — updates as you choose. Final price is confirmed on-site, free with no obligation.</p>
                </div>
              ) : (
                <div className="mt-6 pt-6 border-t border-ink/10" data-testid="summary-pending">
                  <p className="text-xs text-ink/55 leading-relaxed">Pick your garage size to see your live price estimate — it updates as you answer each question.</p>
                </div>
              )}

              {step === 4 && (
                <button type="button" data-testid="goto-contact" onClick={() => setStep(5)}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-rust text-white px-6 py-3.5 font-semibold hover:bg-rust-dark transition-colors">
                  Email Me My Estimate & Book <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
